import type { ReactNode } from 'react'
import { Fragment } from 'react'
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react'
import { useTranslation } from 'next-i18next'
import { twJoin } from 'tailwind-merge'
import ThreeDotsNavIcon from 'shared/icons/three-dots-nav-icon'
import type { TopicInterface } from 'modules/community/types/topic-interface'

interface TopicMenuProps {
  topic: TopicInterface
  onEdit: (topic: TopicInterface) => void
  onDelete: (topicId: number) => void
}

export function TopicMenu({ topic, onEdit, onDelete }: TopicMenuProps): ReactNode {
  const { t } = useTranslation()

  return (
    <Menu as="div" className="relative flex items-center group-hover:visible lg:invisible">
      <MenuButton className="flex items-center">
        <ThreeDotsNavIcon className="size-7 text-text-primary/80 hover:text-text-primary max-lg:rotate-90 max-lg:text-text-primary/60" />
      </MenuButton>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <MenuItems
          as="ul"
          anchor="bottom start"
          className="z-50 flex w-max max-w-sm min-w-36 flex-col rounded-[10px] bg-background-secondary px-5 py-3 shadow-menu"
        >
          <MenuItem as="li" className="p-1">
            {({ focus }) => (
              <button
                type="button"
                className={twJoin(
                  'group flex w-full items-center gap-1.5 rounded-md py-1.5 text-sm',
                  focus ? 'text-text-primary/70' : 'text-text-primary hover:text-text-primary/70',
                )}
                onClick={() => {
                  onEdit(topic)
                }}
              >
                {t('settings.form.update.label')}
              </button>
            )}
          </MenuItem>
          <MenuItem as="li" className="p-1">
            {({ focus }) => (
              <button
                type="button"
                className={twJoin(
                  'group flex w-full items-center gap-1.5 rounded-md py-1.5 text-sm',
                  focus ? 'text-text-primary/70' : 'text-text-primary hover:text-text-primary/70',
                )}
                onClick={() => {
                  onDelete(topic.id)
                }}
              >
                {t('settings.form.delete.label')}
              </button>
            )}
          </MenuItem>
        </MenuItems>
      </Transition>
    </Menu>
  )
}
